import { useI18n } from "@/i18n/I18nProvider";

/**
 * Pill-style tab bar above the product grid.
 * - "All" tab is always present.
 * - Featured categories (WM 2026, Lion Crown, Lion Fierce) shown with gold accent.
 * - Regular categories (Hoodies, T-Shirts) shown with neutral styling.
 */
export default function CategoryTabs({ categories, activeKey, onChange, totalCount }) {
  const { t, lang } = useI18n();

  if (!categories?.length) return null;

  const allLabel =
    lang === "en" ? "All" : lang === "es" ? "Todo" : "Alle";

  return (
    <div
      data-testid="category-tabs"
      className="mb-12 -mx-6 sm:-mx-8 md:mx-0 px-6 sm:px-8 md:px-0 overflow-x-auto scrollbar-none"
    >
      <div className="flex items-center gap-2 sm:gap-3 min-w-max md:min-w-0 md:flex-wrap pb-1">
        <CategoryPill
          label={allLabel}
          count={totalCount}
          active={!activeKey || activeKey === "all"}
          onClick={() => onChange("all")}
          testId="category-tab-all"
        />
        {categories.map((c) => (
          <CategoryPill
            key={c.key}
            label={c.label}
            count={c.count}
            active={activeKey === c.key}
            accent={c.accent}
            featured={c.featured}
            onClick={() => onChange(c.key)}
            testId={`category-tab-${c.key}`}
          />
        ))}
      </div>
    </div>
  );
}

function CategoryPill({ label, count, active, accent, featured, onClick, testId }) {
  const base =
    "shrink-0 flex items-center gap-2 px-4 sm:px-5 py-2.5 border font-body text-[11px] sm:text-xs tracking-[0.2em] uppercase transition-all duration-300 cursor-pointer";

  let styles = "";
  if (active) {
    styles = accent
      ? "border-[#A93043] bg-[#A93043] text-white"
      : "border-[#D4AF37] bg-[#D4AF37] text-black";
  } else if (accent) {
    // Featured / accent tab when not active
    styles =
      "border-[#A93043]/50 text-[#D4AF37] bg-[#A93043]/10 hover:bg-[#A93043]/25 hover:border-[#A93043]";
  } else if (featured) {
    styles =
      "border-[#D4AF37]/40 text-[#D4AF37] hover:bg-[#D4AF37]/15 hover:border-[#D4AF37]";
  } else {
    styles =
      "border-[#1f1f29] text-white/65 hover:border-[#D4AF37]/60 hover:text-white";
  }

  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={testId}
      className={`${base} ${styles}`}
    >
      <span>{label}</span>
      <span
        className={`font-display text-[10px] leading-none ${
          active ? "opacity-80" : "opacity-50"
        }`}
      >
        {String(count).padStart(2, "0")}
      </span>
    </button>
  );
}
